import { FC, MouseEvent, ReactNode } from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import styled, { css, useTheme } from 'styled-components';

import { copyTextToClipboard } from '../../../utils/copy';
import { Div } from '../helpers/StyledUtils';
import { useToasts } from '../Toast';
import Button from './Button';
import Icon, { IconName } from './Icon';
import Text from './Text';

const StyledTooltip = styled.div<{ max_width?: number }>(
  ({ theme, max_width = 344 }) => css`
    max-width: ${max_width}px;
    word-break: break-word;
    white-space: initial;
    width: max-content;
    background-color: ${theme.colors.surface.base.surface};
    box-shadow: ${theme.elevation[3]};
    border-radius: ${theme.radius.normal};
    padding: ${theme.spacing(2)};
  `,
);

export type TooltipCTA = {
  icon?: IconName;
  onClick?: () => void;
  label: string;
  to?: string;
};

// New Tooltip component based on Radix Tooltip but keeping the same API
const Tooltip: FC<{
  open?: boolean;
  align?: 'right' | 'left' | 'center';
  disabled?: boolean;
  side?: 'right' | 'bottom' | 'left' | 'top';
  tooltip?: ReactNode;
  children: ReactNode;
  copyable?: boolean;
  offset?: [number, number];
  fill?: boolean;
  cta?: TooltipCTA;
  max_width?: number;
  as?: React.ElementType;
  element?: ReactNode;
  onHover?: () => void;
  onLeave?: () => void;
  delay?: number;
}> = ({
  open,
  tooltip,
  side = 'right',
  align = 'end',
  children,
  copyable,
  disabled,
  cta,
  offset,
  fill,
  max_width,
  as,
  element = null,
  onHover,
  onLeave,
  delay,
}) => {
  const { addToast } = useToasts();
  const theme = useTheme();
  const cta_link_is_external = cta?.to?.startsWith('http') || false;

  const handleCopyToClipboard = (e: MouseEvent<HTMLParagraphElement>) => {
    e.stopPropagation();

    copyTextToClipboard(String(tooltip)).then((success) =>
      success
        ? addToast('success', `Copied '${String(tooltip)}' to clipboard`)
        : addToast('error', 'Failed to copy'),
    );
  };

  return (
    <RadixTooltip.Provider delayDuration={delay ?? 50}>
      <RadixTooltip.Root
        open={disabled === true ? false : open}
        onOpenChange={
          onHover || onLeave
            ? (open) => {
                if (open) {
                  onHover?.();
                } else {
                  onLeave?.();
                }
              }
            : undefined
        }>
        <RadixTooltip.Trigger disabled={disabled} asChild>
          <Div
            as={as}
            style={{
              display: fill ? 'block' : 'inline-flex',
              width: fill ? '100%' : 'fit-content',
            }}>
            {children}
          </Div>
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            style={{ zIndex: theme.zindex.tooltip }}
            align={align === 'center' ? 'center' : align === 'left' ? 'end' : 'start'}
            side={side}
            sideOffset={offset ? offset[1] : 6}
            alignOffset={offset ? offset[0] : 0}>
            <StyledTooltip
              max_width={max_width}
              onClick={copyable ? handleCopyToClipboard : undefined}
              style={{ cursor: copyable ? 'pointer' : 'default' }}>
              {element || (
                <>
                  <Div flex={{ justify: 'space-between', gap: 2, align: 'center' }}>
                    <Text size="s">{tooltip}</Text>
                    {copyable && <Icon icon="copy" right pointer />}
                  </Div>
                  {cta && (
                    <Button
                      as={cta_link_is_external ? 'a' : undefined}
                      block
                      outline
                      target={cta_link_is_external ? '_blank' : undefined}
                      to={cta_link_is_external ? undefined : cta.to}
                      href={cta.to}
                      icon={cta.icon || 'docs'}
                      onClick={cta.onClick}
                      m={{ t: 3 }}>
                      {cta.label}
                    </Button>
                  )}
                </>
              )}
            </StyledTooltip>
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
};

export default Tooltip;
